/**
 * VectorTrackColorControl
 *
 * A compact Layer Control action for vector-track layers.
 * Allows switching between solid color and attribute-based coloring
 * (SOG, COG, or any annotation key) with a legend.
 *
 * When SOG is selected, users can choose a classification method
 * (Fixed, Natural Breaks, Quantiles, Equal Interval) so the legend
 * adapts to the actual speed distribution in the data.
 *
 * Architecture:
 * - `useTrackColorState` — owns the color mode + classification state
 * - `ColorModeSelector` — renders the mode buttons
 * - `ClassificationSelector` — renders the method picker (SOG only)
 * - `LegendDisplay` — renders the color legend
 * - No domain logic lives in the component tree — it's all in the hook
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiPalette, PiPlus, PiTrash } from 'react-icons/pi';
import type { LayerItem } from '@ais-ui/react-openlayers';
import {
  restyleTrackLayer,
  getTrackDisplayToggles,
  useLayers,
} from '@ais-ui/react-openlayers';
import VectorLayer from 'ol/layer/Vector';
import type Feature from 'ol/Feature';
import type OlGeometry from 'ol/geom/Geometry';
import {
  type TrackColorMode,
  type SogStop,
  TRACK_COLOR_MODE_LABELS,
  SOG_STOPS,
  COG_STOPS,
  getAnnotationKeys,
  buildCategoryPalette,
  buildLegendItems,
  buildSogStopsFromThresholds,
  computeDynamicSogStops,
  type ClassificationMethod,
  CLASSIFICATION_METHODS,
} from '@ais-ui/synthetic-geo';
import type { Track } from '@ais-ui/synthetic-geo';
import {
  COLOR_SCALES,
  DEFAULT_COLOR_SCALE,
  sampleColorScale,
} from '../../utils/colorScales';

// --- Constants ---

const BUILT_IN_MODES: { value: TrackColorMode; label: string }[] = [
  { value: 'track', label: TRACK_COLOR_MODE_LABELS.track },
  { value: 'sog', label: TRACK_COLOR_MODE_LABELS.sog },
  { value: 'cog', label: TRACK_COLOR_MODE_LABELS.cog },
];

/** SOG bucketing method, including a user-defined "custom" option. */
type SogMethod = ClassificationMethod | 'custom';

const SOG_METHODS: { value: SogMethod; label: string }[] = [
  ...CLASSIFICATION_METHODS,
  { value: 'custom', label: 'Custom' },
];

const DEFAULT_SOG_PALETTE = DEFAULT_COLOR_SCALE.value;
const DEFAULT_SOG_THRESHOLDS = SOG_STOPS.map((s) => s.threshold);
const MIN_SOG_BUCKETS = 2;
const MAX_SOG_BUCKETS = 8;

// --- Hook: useTrackColorState ---

interface TrackColorState {
  colorMode: TrackColorMode;
  sogMethod: SogMethod;
  sogPalette: string;
  sogThresholds: number[];
  availableModes: { value: TrackColorMode; label: string }[];
  legend: { color: string; label: string }[] | null;
  setColorMode: (mode: TrackColorMode) => void;
  setSogMethod: (method: SogMethod) => void;
  setSogPalette: (palette: string) => void;
  setSogThresholds: (thresholds: number[]) => void;
}

/**
 * Resolve the SOG threshold values for a given bucketing method.
 * - `custom` uses the user-provided thresholds
 * - `fixed` uses the built-in defaults
 * - dynamic methods derive thresholds from the track's speed distribution
 */
function resolveSogThresholds(
  method: SogMethod,
  customThresholds: number[],
  track: Track | undefined,
): number[] {
  if (method === 'custom') return customThresholds;
  if (method === 'fixed' || !track) return DEFAULT_SOG_THRESHOLDS;
  return computeDynamicSogStops([track], method).map((s) => s.threshold);
}

/** Build SOG colour stops from the current method, thresholds and palette. */
function buildSogStops(
  method: SogMethod,
  customThresholds: number[],
  palette: string,
  track: Track | undefined,
): SogStop[] {
  const thresholds = resolveSogThresholds(method, customThresholds, track);
  const colors = sampleColorScale(palette, thresholds.length);
  return buildSogStopsFromThresholds(thresholds, colors);
}

/**
 * Encapsulates all color-mode logic: mode selection, SOG bucketing
 * (method + palette + custom thresholds), legend building, layer restyling,
 * and persistence so auto-refresh keeps the active styling.
 */
function useTrackColorState(
  layer: LayerItem,
  track: Track | undefined,
  defaultColor: string,
): TrackColorState {
  const { patchLayer } = useLayers();
  const initialMode: TrackColorMode =
    (layer.properties?.colorBy as TrackColorMode) || 'track';
  const [colorMode, setColorModeRaw] = useState<TrackColorMode>(initialMode);
  const [sogMethod, setSogMethodRaw] = useState<SogMethod>(
    () => (layer.properties?.sogMethod as SogMethod) ?? 'fixed',
  );
  const [sogPalette, setSogPaletteRaw] = useState<string>(
    () => (layer.properties?.sogPalette as string) ?? DEFAULT_SOG_PALETTE,
  );
  const [sogThresholds, setSogThresholdsRaw] = useState<number[]>(
    () =>
      (layer.properties?.sogThresholds as number[]) ?? DEFAULT_SOG_THRESHOLDS,
  );

  // Discover all available modes (built-in + annotations from data)
  const availableModes = useMemo(() => {
    if (!track) return BUILT_IN_MODES;
    const annotationKeys = getAnnotationKeys([track]);
    const annotationModes = annotationKeys.map((key) => ({
      value: key as TrackColorMode,
      label: formatAnnotationKey(key),
    }));
    return [...BUILT_IN_MODES, ...annotationModes];
  }, [track]);

  // SOG stops for the current method/palette/thresholds
  const sogStops = useMemo(() => {
    if (colorMode !== 'sog') return undefined;
    return buildSogStops(sogMethod, sogThresholds, sogPalette, track);
  }, [colorMode, sogMethod, sogThresholds, sogPalette, track]);

  // Build legend items for current state
  const legend = useMemo(() => {
    if (colorMode === 'track') return null;
    if (colorMode === 'sog') {
      return (sogStops ?? SOG_STOPS).map((s) => ({
        color: s.color,
        label: s.label,
      }));
    }
    if (colorMode === 'cog') {
      return COG_STOPS.map((s) => ({ color: s.color, label: s.label }));
    }
    if (!track) return null;
    const palette = buildCategoryPalette([track], colorMode);
    return buildLegendItems(colorMode, palette);
  }, [colorMode, sogStops, track]);

  /** Restyle the layer and persist the styling to layer properties. */
  const applyAndPersist = useCallback(
    (mode: TrackColorMode, stops: SogStop[] | undefined) => {
      if (track) {
        restyleTrackLayer(
          layer.layer as VectorLayer<Feature<OlGeometry>>,
          track,
          {
            colorBy: mode,
            color: defaultColor,
            customSogStops: stops,
            label: layer.name,
            ...getTrackDisplayToggles(layer.properties),
          },
        );
      }
      // Persist so auto-refresh re-applies the same styling
      patchLayer(layer.id, {
        properties: {
          colorBy: mode === 'track' ? undefined : mode,
          customSogStops: stops,
        },
      });
    },
    [layer, track, defaultColor, patchLayer],
  );

  const setColorMode = useCallback(
    (mode: TrackColorMode) => {
      setColorModeRaw(mode);
      const stops =
        mode === 'sog'
          ? buildSogStops(sogMethod, sogThresholds, sogPalette, track)
          : undefined;
      applyAndPersist(mode, stops);
    },
    [applyAndPersist, sogMethod, sogThresholds, sogPalette, track],
  );

  const setSogMethod = useCallback(
    (method: SogMethod) => {
      setSogMethodRaw(method);
      patchLayer(layer.id, { properties: { sogMethod: method } });
      applyAndPersist(
        'sog',
        buildSogStops(method, sogThresholds, sogPalette, track),
      );
    },
    [applyAndPersist, sogThresholds, sogPalette, track, patchLayer, layer.id],
  );

  const setSogPalette = useCallback(
    (palette: string) => {
      setSogPaletteRaw(palette);
      patchLayer(layer.id, { properties: { sogPalette: palette } });
      applyAndPersist(
        'sog',
        buildSogStops(sogMethod, sogThresholds, palette, track),
      );
    },
    [applyAndPersist, sogMethod, sogThresholds, track, patchLayer, layer.id],
  );

  const setSogThresholds = useCallback(
    (thresholds: number[]) => {
      setSogThresholdsRaw(thresholds);
      patchLayer(layer.id, { properties: { sogThresholds: thresholds } });
      applyAndPersist(
        'sog',
        buildSogStops('custom', thresholds, sogPalette, track),
      );
    },
    [applyAndPersist, sogPalette, track, patchLayer, layer.id],
  );

  return {
    colorMode,
    sogMethod,
    sogPalette,
    sogThresholds,
    availableModes,
    legend,
    setColorMode,
    setSogMethod,
    setSogPalette,
    setSogThresholds,
  };
}

// --- Helpers ---

function formatAnnotationKey(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// --- Sub-components (composition over configuration) ---

function ColorModeSelector({
  modes,
  activeMode,
  onSelect,
}: {
  modes: { value: TrackColorMode; label: string }[];
  activeMode: TrackColorMode;
  onSelect: (mode: TrackColorMode) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      {modes.map((opt) => (
        <Button
          key={opt.value}
          variant={opt.value === activeMode ? 'default' : 'outline'}
          size="sm"
          className="justify-start text-xs"
          onClick={() => onSelect(opt.value)}
        >
          {opt.label}
        </Button>
      ))}
    </div>
  );
}

function SogMethodSelector({
  method,
  onChange,
}: {
  method: SogMethod;
  onChange: (method: SogMethod) => void;
}) {
  return (
    <div className="mt-2 border-t pt-2">
      <p className="text-xs font-medium mb-1">Buckets</p>
      <Select value={method} onValueChange={(v) => onChange(v as SogMethod)}>
        <SelectTrigger className="h-7 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SOG_METHODS.map((m) => (
            <SelectItem key={m.value} value={m.value} className="text-xs">
              {m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function PaletteSelector({
  palette,
  onChange,
}: {
  palette: string;
  onChange: (palette: string) => void;
}) {
  return (
    <div className="mt-2">
      <p className="text-xs font-medium mb-1">Palette</p>
      <Select value={palette} onValueChange={onChange}>
        <SelectTrigger className="h-7 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {COLOR_SCALES.map((scale) => (
            <SelectItem
              key={scale.value}
              value={scale.value}
              className="text-xs"
            >
              <span className="flex items-center gap-2">
                <span className="inline-flex overflow-hidden rounded-sm">
                  {sampleColorScale(scale.value, 6).map((c, i) => (
                    <span
                      key={i}
                      className="inline-block h-3 w-1.5"
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </span>
                {scale.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Editable list of SOG threshold values (ascending speed buckets).
 *
 * Edits commit immediately so neighbouring buckets are clamped live as the user
 * types, keeping the list monotonically increasing — editing a value pushes the
 * thresholds above it down and the ones below it up, without reordering rows.
 * The actively edited field keeps its raw text so partial input (e.g. an empty
 * field) isn't clobbered mid-edit; on blur it's normalised to the canonical
 * value.
 */
function ThresholdEditor({
  thresholds,
  onChange,
}: {
  thresholds: number[];
  onChange: (thresholds: number[]) => void;
}) {
  const [drafts, setDrafts] = useState<string[]>(() => thresholds.map(String));
  // Index of the field currently being edited, so resyncs don't clobber it.
  const editingIndexRef = useRef<number | null>(null);

  // Re-sync drafts when the committed thresholds change (add/remove/cascade),
  // but preserve the raw text of the field the user is actively editing.
  useEffect(() => {
    setDrafts((prev) =>
      thresholds.map((t, i) =>
        i === editingIndexRef.current ? (prev[i] ?? String(t)) : String(t),
      ),
    );
  }, [thresholds]);

  // Commit drafts to thresholds, clamping neighbours around the edited value
  // so buckets stay increasing.
  const commit = (index: number, draftValues: string[]) => {
    const next = draftValues.map((d) => {
      const n = parseFloat(d);
      return Number.isFinite(n) ? n : 0;
    });
    for (let j = index - 1; j >= 0; j--) {
      if (next[j] > next[j + 1]) next[j] = next[j + 1];
    }
    for (let j = index + 1; j < next.length; j++) {
      if (next[j] < next[j - 1]) next[j] = next[j - 1];
    }
    if (next.some((v, i) => v !== thresholds[i])) {
      onChange(next);
    }
  };

  const handleChange = (index: number, value: string) => {
    editingIndexRef.current = index;
    const nextDrafts = drafts.map((v, i) => (i === index ? value : v));
    setDrafts(nextDrafts);
    commit(index, nextDrafts);
  };

  const handleBlur = () => {
    editingIndexRef.current = null;
    // Normalise display to the canonical committed values.
    setDrafts(thresholds.map(String));
  };

  const removeAt = (index: number) => {
    if (thresholds.length <= MIN_SOG_BUCKETS) return;
    onChange(thresholds.filter((_, i) => i !== index));
  };

  const addBucket = () => {
    if (thresholds.length >= MAX_SOG_BUCKETS) return;
    const last = thresholds[thresholds.length - 1] ?? 0;
    onChange([...thresholds, last + 5]);
  };

  return (
    <div className="mt-2">
      <p className="text-xs font-medium mb-1">Thresholds (kn)</p>
      <div className="flex flex-col gap-1">
        {drafts.map((draft, index) => (
          <div key={index} className="flex items-center gap-1">
            <Input
              type="number"
              value={draft}
              onChange={(e) => handleChange(index, e.target.value)}
              onBlur={handleBlur}
              className="h-7 text-xs"
            />
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 flex-shrink-0"
              onClick={() => removeAt(index)}
              disabled={thresholds.length <= MIN_SOG_BUCKETS}
              aria-label="Remove threshold"
            >
              <PiTrash className="size-4" />
            </Button>
          </div>
        ))}
      </div>
      <Button
        size="sm"
        variant="outline"
        className="mt-1 w-full text-xs"
        onClick={addBucket}
        disabled={thresholds.length >= MAX_SOG_BUCKETS}
      >
        <PiPlus className="mr-1 size-3" /> Add bucket
      </Button>
    </div>
  );
}

function LegendDisplay({
  items,
}: {
  items: { color: string; label: string }[];
}) {
  return (
    <div className="mt-3 border-t pt-2">
      <p className="text-xs font-medium mb-1">Legend</p>
      <div className="flex flex-col gap-0.5">
        {items.map((item, index) => (
          <div
            key={`${index}-${item.label}`}
            className="flex items-center gap-2 text-xs"
          >
            <span
              className="inline-block size-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: item.color }}
            />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Main Component ---

interface VectorTrackColorControlProps {
  layer: LayerItem;
}

export const VectorTrackColorControl = React.memo(
  ({ layer }: VectorTrackColorControlProps) => {
    const [open, setOpen] = useState(false);

    const track = layer.properties?.track as Track | undefined;
    const defaultColor =
      (layer.properties?.defaultColor as string) ?? '#1e88e5';

    const {
      colorMode,
      sogMethod,
      sogPalette,
      sogThresholds,
      availableModes,
      legend,
      setColorMode,
      setSogMethod,
      setSogPalette,
      setSogThresholds,
    } = useTrackColorState(layer, track, defaultColor);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-9 w-9 flex-shrink-0 p-0 m-0"
              >
                <PiPalette className="size-5" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>Track coloring</TooltipContent>
        </Tooltip>
        <PopoverContent className="w-56 p-3" align="start">
          <p className="text-xs font-medium mb-2">Color by</p>

          <ColorModeSelector
            modes={availableModes}
            activeMode={colorMode}
            onSelect={setColorMode}
          />

          {colorMode === 'sog' && (
            <>
              <SogMethodSelector method={sogMethod} onChange={setSogMethod} />
              <PaletteSelector palette={sogPalette} onChange={setSogPalette} />
              {sogMethod === 'custom' && (
                <ThresholdEditor
                  thresholds={sogThresholds}
                  onChange={setSogThresholds}
                />
              )}
            </>
          )}

          {legend && legend.length > 0 && <LegendDisplay items={legend} />}
        </PopoverContent>
      </Popover>
    );
  },
);

VectorTrackColorControl.displayName = 'VectorTrackColorControl';
